import { Table } from '@tanstack/react-table';
import { isFunction } from '@tanstack/react-table';

import { formatNumber } from '../table.helper';
import { TableProps } from '../table.types';

/**
 * Custom hook to handle table selection state and calculations.
 * Abstracts common logic used across action bar and select all components.
 *
 * @param table - TanStack table instance
 * @returns Object containing selection state, computed values, and actions
 */
export function useTableSelection<TData>(table: Table<TData>) {
  const {
    manualPagination = false,
    enablePagination,
    totalRowCount,
    selectAllCounterIsPageSize = false,
    enableRowSelection,
  } = table.options as TableProps<TData>;

  const pageSize = table.getState().pagination.pageSize;
  const isAllSelected = table.getState().isAllSelected;
  const totalSelected = table.getSelectedRowModel().rows.length;

  /**
   * Calculate row count based on pagination context and edge cases
   */
  const calculateRowCount = (): number => {
    let count = table.getRowCount();

    if (enablePagination && manualPagination) {
      count = totalRowCount ?? 0;
    }

    // Edge case: select all means only current page
    if (!selectAllCounterIsPageSize) return count;

    if (isFunction(enableRowSelection)) {
      count = table.getCoreRowModel().rows.filter(enableRowSelection).length;
    } else {
      count = pageSize;
    }

    return count;
  };

  /**
   * Calculate selected count for display (used in SelectedRowCounter)
   */
  const calculateSelectedCount = (): number => {
    // Edge case handling for page-only selection
    if (selectAllCounterIsPageSize) {
      return table.getSelectedRowModel().rows.length;
    }

    const selectedInState = manualPagination
      ? Object.keys(table.getState().rowSelection).length
      : table.getSelectedRowModel().rows.length;

    const isAllRowsSelected = table.getIsAllRowsSelected();

    if (manualPagination && isAllSelected) {
      return totalRowCount ?? 0;
    } else if (isAllRowsSelected) {
      return table.getRowCount();
    } else {
      return selectedInState;
    }
  };

  const rowCount = calculateRowCount();
  const selectedCountNum = calculateSelectedCount();

  return {
    // Computed values
    rowCount,
    totalSelected,
    selectedCount: formatNumber({ value: selectedCountNum }),

    // State flags
    hasSelected: table.getIsSomeRowsSelected() || table.getIsAllRowsSelected(),
    isAllSelected,
    isAllRowsSelected: table.getIsAllRowsSelected(),
    isAllPageRowsSelected: table.getIsAllPageRowsSelected(),
    isSomeSelected: table.getIsSomeRowsSelected(),

    // Options
    manualPagination: manualPagination ?? false,
    selectAllCounterIsPageSize: selectAllCounterIsPageSize ?? false,

    // Actions
    resetSelection: () => table.resetRowSelection(),
  };
}
